import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Loader2, LogOut, Calendar, CheckCircle, AlertCircle, Save, ArrowLeft } from 'lucide-react';

const TrialSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [config, setConfig] = useState({
    enabled: true,
    trial_days: 15
  });

  useEffect(() => {
    fetchTrialConfig();
  }, []);

  const fetchTrialConfig = async () => {
    try {
      const response = await api.get('/admin/trial/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch trial config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnabledToggle = (checked) => {
    setConfig(prev => ({
      ...prev,
      enabled: checked
    }));
  };

  const handleDaysChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 0 && value <= 90) {
      setConfig(prev => ({
        ...prev,
        trial_days: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (config.trial_days < 0 || config.trial_days > 90) {
      setMessage({
        type: 'error',
        text: 'Trial period must be between 0 and 90 days'
      });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);
      
      await api.post('/admin/trial/configure', config);
      
      setMessage({
        type: 'success',
        text: 'Trial configuration saved successfully!'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to save trial configuration'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" 
                alt="SlickSignature"
                className="h-12"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<h1 class="text-2xl font-bold">SlickSignature</h1>';
                }}
              />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-300">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-gray-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="outline"
          onClick={handleBackToAdmin}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Portal
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Free Trial Configuration</CardTitle>
                <CardDescription>
                  Manage the free trial period for new subscriptions
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Status */}
            <Alert className={config.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}>
              {config.enabled ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Free trial is currently <strong>enabled</strong> - New subscriptions get {config.trial_days} days free
                  </AlertDescription>
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 text-gray-600" />
                  <AlertDescription className="text-gray-800">
                    Free trial is currently <strong>disabled</strong> - New subscriptions will be charged immediately
                  </AlertDescription>
                </>
              )}
            </Alert>

            {/* Message Alert */}
            {message && (
              <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                  {message.text}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Enable/Disable Trial */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div>
                  <Label className="text-base font-semibold">Enable Free Trial</Label>
                  <p className="text-sm text-gray-600">
                    Allow new customers to try the service before being charged
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={handleEnabledToggle}
                />
              </div>

              {/* Trial Duration */}
              {config.enabled && (
                <div>
                  <Label htmlFor="trial_days">Trial Duration (Days)</Label>
                  <Input
                    id="trial_days"
                    type="number"
                    min="1"
                    max="90"
                    value={config.trial_days}
                    onChange={handleDaysChange}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Set between 1 and 90 days. Recommended: 7, 14, or 30 days
                  </p>
                </div>
              )}

              <Button
                type="submit"
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </form>

            {/* Information */}
            <div className="border-t pt-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-3">How it works:</h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                  <li>New customers enter payment details during checkout</li>
                  <li>They're not charged until the trial period ends</li>
                  <li>Customers can cancel anytime during the trial</li>
                  <li>After trial ends, they're automatically charged monthly</li>
                </ul>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  💡 Best Practices
                </p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• 7 days: Quick evaluation, faster conversions</li>
                  <li>• 14 days: Balanced approach (current setting)</li>
                  <li>• 30 days: Extended testing, higher engagement</li>
                  <li>• Disabling trial: Only for promotional periods or special offers</li>
                </ul>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  ⚠️ Important Note
                </p>
                <p className="text-xs text-yellow-700">
                  Changes only affect new subscriptions. Existing trials continue with their original terms.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TrialSettings;
